<template>
  <story-preview-item
    :story="primaryStory"
    :tab="tab"
    :versions-count="versionsCount"
    :deleting="deleting"
    @deleted-in-group="onDeletedInGroup"
    @request-folder-delete="onFolderDelete"
    @itemRemoved="$emit('itemRemoved')"
  />
</template>

<script>
import app from 'app';
import Analytics from 'c/WtAnalytics';
import { mapGetters } from 'vuex';
import StoryPreviewItem from 'v/Dashboard/StoryPreviewItem.vue';
import { isAiLock } from '#/const';

// Collapses a version family into a single "current version" card in the
// drafts grid. StoryPreview rows carry folderId/versionNumber but NOT which
// member is active, so we resolve that from the version group (one cheap
// GET /versions per visible family, deduped in the store).
export default {
  name: 'story-preview-group',

  components: {
    StoryPreviewItem,
  },

  props: {
    // Fetched preview rows of this family, in server (lastUpdate desc) order.
    members: {
      type: Array,
      required: true,
    },
    // The family key (root id). Passed in because the root member itself has
    // folderId null, so it cannot always be read off members[0].
    folderId: {
      type: String,
      required: true,
    },
    tab: {
      type: String,
    },
  },

  data() {
    return {
      // Hydrated preview of the active member when it is not among `members`
      // (e.g. it lives on a not-yet-fetched page).
      activePreview: null,
      // A "delete all versions" call is in flight - locks the card (no taps)
      // and shows a per-card deleting overlay instead of the full-screen
      // spinner.
      deleting: false,
    };
  },

  created() {
    this.$_syncGroup();
  },

  watch: {
    // The drafts list refreshes while the card is mounted (polling, AI job
    // progress). Re-sync so a member added server-side is picked up.
    members() {
      this.$_syncGroup();
    },
  },

  computed: {
    ...mapGetters('storyVersions', ['groupByFolderId', 'isLockedByOther']),

    group() {
      return this.groupByFolderId(this.folderId);
    },

    primaryStory() {
      // While a version of this family is being AI-generated, the backend
      // marks that version locked by the AI user. Surface it so the folder
      // card shows the "AI story creation in progress" state instead of
      // hiding it behind the (still-unchanged) active version.
      const aiLocked = this.members.find((m) =>
        isAiLock({ userId: m.lockedUserId, userName: m.lockedUserName })
      );
      if (aiLocked) {
        return aiLocked;
      }

      const activeId = this.group ? this.group.activeStoryId : null;

      if (activeId) {
        const inMembers = this.members.find((m) => m.id === activeId);
        if (inMembers) {
          return inMembers;
        }
        if (this.activePreview && this.activePreview.id === activeId) {
          return this.activePreview;
        }
      }

      // Fallback until /versions resolves (or the active preview loads):
      // the most-recently-modified fetched member (server order is desc).
      return this.members[0];
    },

    versionsCount() {
      if (!this.group) {
        return this.members.length;
      }
      // The fetched rows are a lower bound on the real version count. While a
      // stale group is being force-refetched (e.g. an Edit-with-AI version the
      // cache predates), this keeps the badge correct instead of waiting a
      // round-trip on the old count.
      return Math.max(this.group.versions.length, this.members.length);
    },
  },

  methods: {
    $_syncGroup() {
      // A cached group is stale when the drafts list shows a family member the
      // group doesn't know about - e.g. the version an Edit-with-AI job just
      // created (the group was cached while the source was still standalone,
      // so versionsCount would stay 1 and the count badge would never show).
      // Force a refetch in that case; otherwise the cache is used as-is.
      const cached = this.group;
      const force =
        !!cached &&
        (this.members.some(
          (m) => !cached.versions.some((v) => v.storyId === m.id)
        ) ||
          // ...or the cached active member is no longer among the drafts - e.g.
          // it was just produced and left the group. Without a refetch the card
          // would keep showing (and navigating to) a ghost of the old active.
          // Pagination can also put a real active on a not-yet-fetched page;
          // the forced refetch is then just one redundant GET.
          (!!cached.activeStoryId &&
            !this.members.some((m) => m.id === cached.activeStoryId)));

      this.$store
        .dispatch('storyVersions/FETCH_VERSIONS', {
          storyId: this.members[0].id,
          folderId: this.folderId,
          force,
        })
        .then(() => this.$_ensureActivePreview())
        .catch(() => {});
    },

    $_ensureActivePreview() {
      const activeId = this.group ? this.group.activeStoryId : null;
      if (!activeId || this.members.some((m) => m.id === activeId)) {
        return;
      }
      // The active version is on a not-yet-fetched page. Build a preview-shaped
      // object from the version payload (it carries thumbnailUrl + lock) so no
      // extra fetch is needed.
      const version = this.group.versions.find((v) => v.storyId === activeId);
      if (version) {
        this.activePreview = this.$_versionToPreview(version);
      }
    },

    $_versionToPreview(v) {
      return {
        id: v.storyId,
        title: v.title,
        thumbnailPath: v.thumbnailUrl,
        // lastModifiedDate may be an ISO string; StoryPreviewItem does a
        // raw numeric subtraction, so normalize to epoch ms here.
        lastUpdate: app.moment(v.lastModifiedDate).valueOf(),
        folderId: this.folderId,
        versionNumber: v.versionNumber,
        type: v.type,
        locked: this.isLockedByOther(v),
        // Pass the locker id so StoryPreviewItem can detect the AI lock by id
        // (the versions API name may be null or differ from the drafts one).
        lockedUserId: v.lockedByUserId,
        lockedUserName:
          v.lockedByUserName || app.culture.storyVersions.unknownUser,
      };
    },

    onDeletedInGroup(deletedStoryId) {
      // The member shown on the grid card was deleted. Refetch the group so the
      // card reflects the server-side reshape (or dissolves to standalone).
      this.$store.dispatch('storyVersions/REFRESH_AFTER_DELETE', {
        deletedStoryId,
        folderId: this.folderId,
      });
      // Drop any stale hydrated active preview; it re-resolves on next render.
      this.activePreview = null;
    },

    // Folder ("delete all versions") flow - a modal confirmation, not the
    // inline thumbnail confirm used for standalone drafts.
    onFolderDelete() {
      const name = this.primaryStory.title || 'Untitled';
      const count = this.versionsCount;

      const nameHtml = `<span class="wt2-color-error">${this.$_escapeHtml(
        name
      )}</span>`;
      const countHtml = `<span class="wt2-color-error">${count} ${app.culture.storyVersions.versionsCount}</span>`;
      const description = app.culture.storyVersions.deleteFolderText
        .replace('{name}', nameHtml)
        .replace('{count}', countHtml);

      app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_3,
          title: app.culture.storyVersions.deleteFolderTitle,
          titleColorClass: 'wt2-color-normal',
          description,
          buttonNoText: app.culture.generic.CANCEL,
          buttonYesText: app.culture.storyVersions.deleteFolderConfirm,
          primaryBtnType: 'error',
        })
        .then((data) => {
          if (data.action) {
            this.$_deleteAllVersions();
          }
        });
    },

    $_deleteAllVersions() {
      // Lock just this card (block taps + show the deleting overlay) instead
      // of the full-screen spinner. Restored on failure below; on success the
      // card is removed with its rows.
      this.deleting = true;

      // One server call deletes the whole group (root + every version). The
      // response lists the deleted ids, so we drop just those rows from the
      // store - the grid reflows reactively (deleteStory removes from BOTH the
      // Drafts and Home lists). No full list refetch.
      app.api.draftStories
        .deleteFolder(this.folderId)
        .done((res) => {
          const ids =
            res && res.deletedStoryIds && res.deletedStoryIds.length
              ? res.deletedStoryIds
              : this.$_memberIds();
          this.$_removeRows(ids);
          Analytics.trigger('analytics:versions:delete', {
            context: 'dashboard',
            scope: 'folder',
          });
        })
        .fail((err) => {
          // Nothing was deleted - unlock the card and re-enable taps.
          this.deleting = false;
          const code = err && err.errorCode;

          if (code === 423) {
            // All-or-nothing on locks: nothing was deleted and the group is
            // untouched. Stricter than a single-version delete - even the
            // requester's own open editor tab blocks it.
            app.dialog.error({
              type: app.dialog.types.ERROR_1,
              title: app.culture.storyVersions.deleteFolderLocked,
            });
          } else if (code === 404) {
            // The group could not be found - it may already be deleted, or it
            // dissolved into a standalone draft whose stories STILL EXIST. We
            // can't tell which from a 404, so never remove rows here (that
            // would wipe valid drafts). Just tell the user; they can refresh.
            app.dialog.error({
              type: app.dialog.types.ERROR_1,
              title: app.culture.storyVersions.deleteFolderNotFound,
            });
          }
          // Other statuses already showed a serverError dialog in the service.
        });
    },

    $_removeRows(ids) {
      ids.forEach((id) => this.$store.commit('deleteStory', id));
      this.$store.commit('storyVersions/removeGroup', this.folderId);
    },

    $_memberIds() {
      return this.group
        ? this.group.versions.map((v) => v.storyId)
        : this.members.map((m) => m.id);
    },

    $_escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
  },
};
</script>
